import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Survey, SurveyQuestion } from "posthog-js";
import type { SurveyResponses } from "../../hooks/usePostHogSurvey";
import { SurveyQuestionType } from "../../constants/survey";
import SingleChoiceQuestion from "./SingleChoiceQuestion";
import RatingQuestion from "./RatingQuestion";
import OpenQuestion from "./OpenQuestion";

/**
 * Branching configuration for response-based question flow.
 * Maps choice index (as string) to either a question index (number) or "end" to complete survey.
 */
interface ResponseBasedBranching {
  type: "response_based";
  responseValues: Record<string, number | "end">;
}

/**
 * Union type for all branching configurations.
 * Currently only response_based is supported.
 */
type QuestionBranching = ResponseBasedBranching;

/**
 * Extended survey question type that includes optional branching configuration.
 * PostHog's SurveyQuestion type doesn't include branching, so we use intersection.
 */
type SurveyQuestionWithBranching = SurveyQuestion & {
  branching?: QuestionBranching;
};

/**
 * Props for the FeedbackSurveyDialog component.
 *
 * @property {Survey} survey - The survey data to display, typically from PostHog.
 * @property {boolean} open - Whether the dialog/modal is open.
 * @property {SurveyResponses} responses - The user's current responses keyed by question id.
 * @property {(questionId: string, value: string | number | null) => void} onResponseChange - Called when a question answer changes.
 * @property {() => void} onSubmit - Called when the user submits the survey.
 * @property {() => void} onDismiss - Called when the dialog is dismissed without submitting.
 * @property {string} [formTitle] - Optional custom title for the form. Defaults to survey.name.
 * @property {string} [lastPageTitle] - Optional custom title for the last page. Defaults to "Thank you!".
 * @property {string} [lastPageMessage] - Optional custom message for the last page (supports markdown). Defaults to "We appreciate your feedback. Your responses help us improve.".
 */
interface FeedbackSurveyDialogProps {
  /** The survey data from PostHog */
  survey: Survey;
  /** Whether the dialog is open */
  open: boolean;
  /** Current responses */
  responses: SurveyResponses;
  /** Callback to update a response */
  onResponseChange: (questionId: string, value: string | number | null) => void;
  /** Callback when survey is submitted */
  onSubmit: () => void;
  /** Callback when survey is dismissed */
  onDismiss: () => void;
  /** Optional custom title for the form. Defaults to survey.name */
  formTitle?: string;
  /** Optional custom title for the last page. Defaults to "Thank you!" */
  lastPageTitle?: string;
  /** Optional custom message for the last page (supports markdown). Defaults to "We appreciate your feedback. Your responses help us improve." */
  lastPageMessage?: string;
}

/**
 * Separator used in open choice responses (e.g., "Other: custom text")
 */
const OPEN_CHOICE_SEPARATOR = ": ";

/**
 * FeedbackSurveyDialog Component
 *
 * Renders a multi-question survey dialog as a stepper (one question at a time),
 * allowing users to navigate forward/back between questions.
 * Questions supported: single choice, numeric rating, open (text).
 * Shows progress and supports required/optional questions.
 * Supports question branching based on response values.
 */
const FeedbackSurveyDialog: React.FC<FeedbackSurveyDialogProps> = ({
  survey,
  open,
  responses,
  onResponseChange,
  onSubmit,
  onDismiss,
  formTitle,
  lastPageTitle = "Thank you!",
  lastPageMessage = "We appreciate your feedback. Your responses help us improve.",
}) => {
  // Index of the currently visible question in this session
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Track if we're showing the last page
  const [showLastPage, setShowLastPage] = useState(false);
  // Track navigation history for back button (stores question indices)
  const [questionHistory, setQuestionHistory] = useState<number[]>([0]);

  // All questions for this survey (cast to include branching type)
  const questions = survey.questions as SurveyQuestionWithBranching[];
  // Count of total questions (for progress and navigation)
  const totalQuestions = questions.length;
  // Currently displayed question
  const currentQuestion = questions[currentQuestionIndex];
  // Each question should have a stable id (fallback: numbered string key)
  const questionId = currentQuestion.id ?? `question-${currentQuestionIndex}`;
  // Overall progress (0-100) for the stepper/progress bar
  // If showing last page, progress is 100%, otherwise calculate based on history length
  const progress = showLastPage
    ? 100
    : (questionHistory.length /
        Math.max(questionHistory.length, totalQuestions)) *
      100;

  // The current response value for this question, if any
  const currentResponse = responses[questionId];
  // Is this question optional? Affects validation and placeholders.
  const isOptional = "optional" in currentQuestion && currentQuestion.optional;
  // Does the user have a response, for disabling/enabling "Next/Submit" button
  const hasResponse =
    currentResponse !== null &&
    currentResponse !== undefined &&
    currentResponse !== "";

  /**
   * Determines the next question index based on branching configuration.
   * Returns the next question index, or "end" if the survey should end.
   *
   * @param question - The current question with potential branching
   * @param response - The user's response to the current question
   * @param currentIndex - The current question index
   * @returns Next question index or "end"
   */
  const getNextQuestionTarget = useCallback(
    (
      question: SurveyQuestionWithBranching,
      response: string | number | null,
      currentIndex: number
    ): number | "end" => {
      // If no branching, go to next question or end
      if (!question.branching) {
        return currentIndex + 1 < totalQuestions ? currentIndex + 1 : "end";
      }

      // Handle response-based branching
      // Cast to the branching type since PostHog's types don't include branching
      const branching = question.branching as QuestionBranching;
      if (branching.type === "response_based") {
        // Get the choice index from the response
        if ("choices" in question && question.choices && response !== null) {
          // For open choice responses, extract the base choice (before separator)
          const responseStr = String(response);
          const baseChoice = responseStr.includes(OPEN_CHOICE_SEPARATOR)
            ? responseStr.split(OPEN_CHOICE_SEPARATOR)[0]
            : responseStr;

          const choiceIndex = (question.choices as string[]).findIndex(
            (choice: string) => choice === baseChoice
          );

          if (choiceIndex !== -1) {
            const target = branching.responseValues[String(choiceIndex)];
            if (target !== undefined) {
              return target;
            }
          }
        }
      }

      // Default: go to next question or end
      return currentIndex + 1 < totalQuestions ? currentIndex + 1 : "end";
    },
    [totalQuestions]
  );

  /**
   * Handler for the "Next" button. Determines next question based on branching.
   * If target is "end" or past last question, shows last page.
   */
  const handleNext = () => {
    const target = getNextQuestionTarget(
      currentQuestion,
      currentResponse,
      currentQuestionIndex
    );

    if (
      target === "end" ||
      (typeof target === "number" && target >= totalQuestions)
    ) {
      // Show last page instead of immediately submitting
      setShowLastPage(true);
    } else {
      setCurrentQuestionIndex(target);
      setQuestionHistory((prev) => [...prev, target]);
    }
  };

  /**
   * Handler for the "Back" button. Uses history to navigate back.
   * If on last page, goes back to last visited question.
   */
  const handleBack = () => {
    if (showLastPage) {
      setShowLastPage(false);
    } else if (questionHistory.length > 1) {
      // Pop current question from history and go to previous
      const newHistory = [...questionHistory];
      newHistory.pop();
      const previousIndex = newHistory[newHistory.length - 1];
      setCurrentQuestionIndex(previousIndex);
      setQuestionHistory(newHistory);
    }
  };

  /**
   * Handler for closing the last page. Submits the survey.
   */
  const handleLastPageClose = () => {
    onSubmit();
  };

  /**
   * Renders the last page message with markdown support.
   */
  const renderLastPageMessage = () => {
    return (
      <Box
        sx={{
          "& p": {
            margin: 0,
            color: "text.secondary",
          },
          "& p + p": {
            marginTop: 1,
          },
          "& a": {
            color: "text.primary",
            textDecoration: "underline",
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        >
          {lastPageMessage}
        </ReactMarkdown>
      </Box>
    );
  };

  /**
   * Determines which question component to render, based on question type.
   * Falls back to a generic message if the type is unsupported.
   */
  const renderQuestion = () => {
    switch (currentQuestion.type) {
      case SurveyQuestionType.SINGLE_CHOICE:
        return (
          <SingleChoiceQuestion
            question={currentQuestion}
            value={currentResponse as string | null}
            onChange={(value) => onResponseChange(questionId, value)}
          />
        );
      case SurveyQuestionType.RATING:
        return (
          <RatingQuestion
            question={currentQuestion}
            value={currentResponse as number | null}
            onChange={(value) => onResponseChange(questionId, value)}
          />
        );
      case SurveyQuestionType.OPEN:
        return (
          <OpenQuestion
            question={currentQuestion}
            value={currentResponse as string | null}
            onChange={(value) => onResponseChange(questionId, value)}
          />
        );
      default:
        // Fallback in case a new or unsupported question type is encountered
        return (
          <Typography color="text.secondary">
            Unsupported question type
          </Typography>
        );
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      {/* Survey Title */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {formTitle ?? survey.name}
        </Typography>
      </DialogTitle>

      {/* Linear progress bar for multi-step indication */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mx: 3, borderRadius: 1 }}
      />

      {/* Survey question content or last page */}
      <DialogContent sx={{ pt: 3 }}>
        {showLastPage ? (
          // Last page content
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              py: 4,
            }}
          >
            <Typography variant="h5" fontWeight={500} sx={{ mb: 2 }}>
              {lastPageTitle}
            </Typography>
            {renderLastPageMessage()}
          </Box>
        ) : (
          // Question content
          <>
            {/* Question step indicator */}
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Question {questionHistory.length}
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={500}
              sx={{ mb: 2, mt: 0.5 }}
            >
              {currentQuestion.question}
              {/* Optional label if the question is not required */}
              {isOptional && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (Optional)
                </Typography>
              )}
            </Typography>
            {/* Render the appropriate input for this question */}
            {renderQuestion()}
          </>
        )}
      </DialogContent>

      {/* Stepper Navigation & Submit */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {showLastPage ? (
          // Last page: only show close button
          <Button
            onClick={handleLastPageClose}
            variant="contained"
            fullWidth
            disableRipple
          >
            Close
          </Button>
        ) : (
          // Question screen: show back and next/submit buttons
          <>
            {/* Back button (disabled if at first question in history) */}
            <Button
              onClick={handleBack}
              disabled={questionHistory.length <= 1}
              sx={{ mr: "auto" }}
              disableRipple
            >
              Back
            </Button>
            {/* Next/Submit button (disabled if not answered and required) */}
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!hasResponse && !isOptional}
              disableRipple
            >
              {(() => {
                // Determine if clicking next would end the survey
                const target = getNextQuestionTarget(
                  currentQuestion,
                  currentResponse,
                  currentQuestionIndex
                );
                const willEndSurvey =
                  target === "end" ||
                  (typeof target === "number" && target >= totalQuestions);
                return willEndSurvey ? "Submit" : "Next";
              })()}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackSurveyDialog;
